import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart
} from 'recharts';

const BalanceGraph = ({ transactions, bets }) => {
  const { t, language } = useLanguage();

  // Build daily balance history from transactions
  const buildBalanceHistory = () => {
    if (!transactions || transactions.length === 0) {
      const history = [];
      const today = new Date();
      const startDate = new Date();
      startDate.setDate(today.getDate() - 7);
      
      const current = new Date(startDate);
      current.setHours(0, 0, 0, 0);
      const end = new Date(today);
      end.setHours(0, 0, 0, 0);
      
      while (current <= end) {
        const labelDate = current.toLocaleDateString(undefined, {
          day: '2-digit',
          month: '2-digit'
        });
        history.push({
          date: labelDate,
          balance: 0,
          netChange: 0,
          hasTransactions: false
        });
        current.setDate(current.getDate() + 1);
      }
      return history;
    }

    // Sort transactions by date (oldest first)
    const sorted = [...transactions].sort(
      (a, b) => new Date(a.created_at) - new Date(b.created_at)
    );

    let runningBalance = 0;
    const dailyBalances = {};
    const dailyNetChanges = {};

    sorted.forEach((tx) => {
      if (tx.reference_type === 'bet' && tx.bet_status === 'pending') {
        return;
      }
      runningBalance += tx.amount;
      const d = new Date(tx.created_at);
      const dateKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      
      dailyBalances[dateKey] = runningBalance;
      dailyNetChanges[dateKey] = (dailyNetChanges[dateKey] || 0) + tx.amount;
    });

    const oldestDate = new Date(sorted[0].created_at);
    let startDate = new Date(oldestDate);
    startDate.setDate(startDate.getDate() - 1); // Start 1 day before first transaction to show initial 0€ state
    
    // Ensure we show at least a 7-day window if history is short
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    if (startDate > sevenDaysAgo) {
      startDate = sevenDaysAgo;
    }

    const today = new Date();
    const latestTxDate = new Date(sorted[sorted.length - 1].created_at);
    const end = new Date(Math.max(today, latestTxDate));
    
    const current = new Date(startDate);
    current.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);

    const history = [];
    let lastBalance = 0;

    while (current <= end) {
      const dateKey = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
      
      if (dailyBalances[dateKey] !== undefined) {
        lastBalance = dailyBalances[dateKey];
      }
      
      const netChange = dailyNetChanges[dateKey] || 0;
      const labelDate = current.toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit'
      });

      history.push({
        date: labelDate,
        dateKey: dateKey,
        balance: lastBalance,
        netChange: netChange,
        hasTransactions: dailyBalances[dateKey] !== undefined
      });

      current.setDate(current.getDate() + 1);
    }

    return history;
  };

  const data = buildBalanceHistory();
  
  // Calculate net change over the visible period to determine trend color (green vs red vs neutral grey)
  const startBalance = data.length > 0 ? data[0].balance : 0;
  const currentBalance = data.length > 0 ? data[data.length - 1].balance : 0;
  const themeColor = currentBalance > startBalance 
    ? "#22c55e" 
    : currentBalance < startBalance 
      ? "#ef4444" 
      : "#94a3b8";

  // Dynamic YAxis domain calculation
  const balances = data.map(d => d.balance);
  const minBalance = Math.min(...balances, 0);
  const maxBalance = Math.max(...balances, 10);
  const range = maxBalance - minBalance;
  const pad = Math.max(10, range * 0.15);
  const yDomain = [Math.floor(minBalance - pad), Math.ceil(maxBalance + pad)];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <div className="bg-card/90 backdrop-blur-md border border-border rounded-xl p-3 shadow-2xl animate-in fade-in duration-200">
          <p className="text-xs text-muted-foreground font-sans font-medium mb-1.5">{label}</p>
          <div className="space-y-1.5">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-sans font-bold">
                {t('netProfitLoss')}
              </p>
              <p className={`text-base font-mono font-bold ${dataPoint.balance >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {dataPoint.balance.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { 
                  minimumFractionDigits: 2, 
                  maximumFractionDigits: 2 
                })} €
              </p>
            </div>
            {dataPoint.hasTransactions && (
              <div className="pt-1.5 border-t border-border mt-1">
                <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-sans font-bold">
                  {t('activity') || 'Aktivität'}
                </p>
                <p className={`text-xs font-mono font-bold ${dataPoint.netChange > 0 ? 'text-green-400' : dataPoint.netChange < 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
                  {dataPoint.netChange > 0 ? '+' : ''}
                  {dataPoint.netChange.toLocaleString(language === 'de' ? 'de-DE' : 'en-US', { 
                    minimumFractionDigits: 2, 
                    maximumFractionDigits: 2 
                  })} €
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
        <defs>
          <linearGradient id="colorBalance" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={themeColor} stopOpacity={0.2}/>
            <stop offset="95%" stopColor={themeColor} stopOpacity={0}/>
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
        <XAxis 
          dataKey="date" 
          stroke="hsl(var(--muted-foreground))" 
          fontSize={10}
          tickLine={false}
          axisLine={false}
          minTickGap={30}
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        />
        <YAxis 
          stroke="hsl(var(--muted-foreground))" 
          fontSize={10}
          tickLine={false}
          axisLine={false}
          domain={yDomain}
          tickFormatter={(value) => `${value.toLocaleString(language === 'de' ? 'de-DE' : 'en-US')} €`}
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        />
        <Tooltip content={<CustomTooltip />} />
        <ReferenceLine y={0} stroke="hsl(var(--border))" strokeDasharray="3 3" />
        <Area
          type="linear"
          dataKey="balance"
          stroke={themeColor}
          strokeWidth={2}
          fill="url(#colorBalance)"
          animationDuration={800}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default BalanceGraph;
